/* eslint-disable no-underscore-dangle */

/**
 * Local-only page-load diagnostics (Phase 2 of the student performance epic).
 *
 * Off by default. Turns on with `?perf=1` (remembered in localStorage until `?perf=0`),
 * and only in the local environment, as on the server. When off, every exported function
 * returns immediately: no header, no observers, no mixin, no network calls.
 *
 * One "page" is a full load or a router navigation. The module records the page's
 * requests, long tasks, layout shift, JS/CSS resources and component churn, then POSTs
 * one JSON object to `/api/perf/pages` once the page is ready. It never sends request
 * bodies, response bodies, headers, or query values beyond `include`, `page`, `per_page`.
 */

const STORAGE_KEY = 'edverum.perf';
const REPORT_URL = '/api/perf/pages';
const PAGE_HEADER = 'X-Perf-Page';
const IDLE_MS = 500;
const TIMEOUT_MS = 30000;
const POLL_MS = 100;
const TOP_COMPONENTS = 25;
const SENSITIVE_PARAMS = /^(token|key|password|secret|signature)$/i;
const SAFE_VALUE_PARAMS = new Set(['include', 'page', 'per_page']);

let enabled = null;
let http = null;
let reportingStopped = false;
let page = null;
let requestSequence = 0;
const requests = new Map();

const now = () => performance.now();

const makeId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};

const readFlag = () => {
    if (typeof window === 'undefined' || window.__INITIAL_STATE__?.Env !== 'local') {
        return false;
    }

    try {
        const requested = new URLSearchParams(window.location.search).get('perf');

        if (requested === '1') {
            window.localStorage.setItem(STORAGE_KEY, '1');
        } else if (requested === '0') {
            window.localStorage.removeItem(STORAGE_KEY);
        }

        return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        return false;
    }
};

export const isPerfEnabled = () => {
    if (enabled === null) {
        enabled = readFlag();
    }

    return enabled;
};

const hashString = value => {
    let hash = 5381;

    for (let i = 0; i < value.length; i += 1) {
        hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
    }

    return (hash >>> 0).toString(16);
};

const flattenParams = (params, prefix = '') => {
    const pairs = [];

    Object.entries(params || {}).forEach(([name, value]) => {
        const key = prefix ? `${prefix}[${name}]` : name;

        if (Array.isArray(value)) {
            pairs.push([`${key}[]`, value.join(',')]);
        } else if (value !== null && typeof value === 'object') {
            pairs.push(...flattenParams(value, key));
        } else if (value !== undefined) {
            pairs.push([key, String(value)]);
        }
    });

    return pairs;
};

const parseRequestUrl = config => {
    const parsed = new URL(config.url || '/', 'http://perf.local');
    const pairs = [...parsed.searchParams.entries(), ...flattenParams(config.params)];

    return { path: parsed.pathname, pairs };
};

/**
 * Path plus query keys. Values are kept only for `include`, `page` and `per_page`.
 */
export const describeRequestUrl = config => {
    const { path, pairs } = parseRequestUrl(config);
    const parts = pairs.map(([key, value]) => (SAFE_VALUE_PARAMS.has(key) ? `${key}=${value}` : key));

    return parts.length ? `${path}?${parts.join('&')}` : path;
};

/**
 * Duplicate key: method, path and the sorted full query. Query values are reduced to a
 * hash so two calls with the same values match without exposing the values.
 */
const duplicateKey = config => {
    const { path, pairs } = parseRequestUrl(config);
    const query = pairs
        .map(([key, value]) => `${key}=${value}`)
        .sort()
        .join('&');

    return `${(config.method || 'get').toUpperCase()} ${path}${query ? `?#${hashString(query)}` : ''}`;
};

/**
 * Page URL with the values of sensitive query parameters removed.
 */
export const sanitizePageUrl = location => {
    const params = new URLSearchParams(location.search || '');

    [...params.keys()].forEach(name => {
        if (SENSITIVE_PARAMS.test(name)) {
            params.set(name, '[redacted]');
        }
    });

    const query = params.toString();

    return `${location.pathname}${query ? `?${query}` : ''}`;
};

const newCounters = () => ({ longTasks: [], layoutShift: 0, mounted: {}, updated: {} });

const stopPolling = () => {
    if (page?.timer) {
        clearInterval(page.timer);
        page.timer = null;
    }
};

const topCounts = counts =>
    Object.fromEntries(
        Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, TOP_COMPONENTS)
    );

const stringParams = params =>
    Object.fromEntries(Object.entries(params || {}).map(([key, value]) => [key, String(value)]));

const resourceList = (extension, since) =>
    performance
        .getEntriesByType('resource')
        .filter(entry => entry.startTime >= since && !['xmlhttprequest', 'fetch'].includes(entry.initiatorType))
        .filter(entry => {
            try {
                return new URL(entry.name, window.location.href).pathname.endsWith(extension);
            } catch {
                return false;
            }
        })
        .map(entry => ({
            name: new URL(entry.name, window.location.href).pathname,
            bytes: entry.transferSize || entry.encodedBodySize || null,
            duration_ms: Math.round(entry.duration),
        }));

const buildReport = (reason, readyAt) => {
    const records = page.requestIds.map(id => requests.get(id)).filter(Boolean);
    const counts = {};

    records.forEach(record => {
        counts[record.key] = (counts[record.key] || 0) + 1;
    });

    return {
        page_id: page.id,
        kind: page.kind,
        url: sanitizePageUrl(window.location),
        route_name: page.route?.name ?? null,
        route_params: stringParams(page.route?.params),
        ready_ms: Math.round(readyAt - page.startedAt),
        ready_reason: reason,
        requests: records.map(record => ({
            method: record.method,
            url: record.url,
            status: record.status,
            duration_ms: Math.round(record.duration),
            bytes: record.bytes,
            retries: record.retries,
            started_ms: Math.round(record.startedAt - page.startedAt),
        })),
        duplicate_requests: Object.entries(counts)
            .filter(([, count]) => count >= 2)
            .map(([key, count]) => ({ key, count })),
        long_tasks: page.counters.longTasks,
        layout_shift: Math.round(page.counters.layoutShift * 10000) / 10000,
        resources: {
            js: resourceList('.js', page.startedAt),
            css: resourceList('.css', page.startedAt),
        },
        components: {
            mounted: topCounts(page.counters.mounted),
            updated: topCounts(page.counters.updated),
        },
        second_ready_marker: null,
    };
};

const sendReport = async body => {
    if (reportingStopped || !http) {
        return;
    }

    try {
        await http.post(REPORT_URL, body, { perfIgnore: true, errorHandle: false, retry: false });
    } catch (error) {
        const status = error?.response?.status;

        if (status === 404 || status === 403) {
            reportingStopped = true;
        }
    }
};

const finishPage = reason => {
    if (!page || page.reported) {
        return;
    }

    page.reported = true;
    stopPolling();
    sendReport(buildReport(reason, now()));
};

const checkReady = () => {
    if (!page || page.reported || !page.navigationEnded) {
        return;
    }

    const elapsed = now() - page.startedAt;

    if (document.querySelector('[data-perf-ready]')) {
        finishPage('marker');
    } else if (page.inflight === 0 && now() - page.lastActivityAt >= IDLE_MS) {
        finishPage('network-idle');
    } else if (elapsed >= TIMEOUT_MS) {
        finishPage('timeout');
    }
};

/**
 * Begin a new page. A navigation that is the app's first route resolution belongs to the
 * full-load page and does not start a new one.
 */
export const startPage = ({ kind, route = null, from = null } = {}) => {
    if (!isPerfEnabled()) {
        return;
    }

    const initialNavigation =
        kind === 'navigation' && page?.kind === 'full-load' && !page.reported && !from?.matched?.length;

    if (initialNavigation) {
        page.route = route;

        return;
    }

    if (kind === 'navigation' && from && route && from.fullPath === route.fullPath) {
        return;
    }

    stopPolling();

    const fullLoad = kind === 'full-load';
    const serverId = fullLoad
        ? performance.getEntriesByType('navigation')[0]?.serverTiming?.find(entry => entry.name === 'perf-page')
              ?.description
        : null;

    page = {
        id: serverId || makeId(),
        kind,
        route,
        startedAt: fullLoad ? 0 : now(),
        lastActivityAt: now(),
        inflight: 0,
        navigationEnded: false,
        reported: false,
        requestIds: [],
        counters: newCounters(),
        timer: setInterval(checkReady, POLL_MS),
    };
};

/**
 * Called from the router's afterEach. The ready checks only run once the navigation ended.
 */
export const markNavigationEnd = () => {
    if (isPerfEnabled() && page) {
        page.navigationEnded = true;
        page.lastActivityAt = now();
    }
};

export const markPageReady = reason => {
    if (isPerfEnabled()) {
        finishPage(reason || 'marker');
    }
};

export const trackRequestStart = config => {
    if (!isPerfEnabled() || config.perfIgnore || !page) {
        return;
    }

    if (typeof config.headers?.set === 'function') {
        config.headers.set(PAGE_HEADER, page.id);
    } else if (config.headers) {
        config.headers[PAGE_HEADER] = page.id;
    }

    let record = config.__perfRequestId ? requests.get(config.__perfRequestId) : null;

    if (record && record.done) {
        record.done = false;

        // A retry can start after navigation has opened a new page. Only the page
        // that owns the request counts it, or the new page's idle timer drifts.
        if (page.requestIds.includes(config.__perfRequestId)) {
            page.inflight += 1;
            page.lastActivityAt = now();
        }

        return;
    }

    requestSequence += 1;
    const id = `r${requestSequence}`;

    record = {
        key: duplicateKey(config),
        method: (config.method || 'get').toUpperCase(),
        url: describeRequestUrl(config),
        startedAt: now(),
        done: false,
        status: 0,
        duration: 0,
        bytes: null,
        retries: 0,
    };

    config.__perfRequestId = id;
    requests.set(id, record);
    page.requestIds.push(id);
    page.inflight += 1;
    page.lastActivityAt = now();
};

const readHeader = (headers, name) => {
    if (!headers) {
        return null;
    }

    const value = typeof headers.get === 'function' ? headers.get(name) : headers[name];

    return value === undefined || value === null ? null : value;
};

export const trackRequestEnd = (config, responseOrError) => {
    if (!isPerfEnabled() || !config?.__perfRequestId) {
        return;
    }

    const record = requests.get(config.__perfRequestId);

    if (!record || record.done) {
        return;
    }

    const response = responseOrError?.response ?? responseOrError;
    const length = Number(readHeader(response?.headers, 'content-length'));

    record.done = true;
    record.status = response?.status ?? 0;
    record.duration = response?.duration ?? now() - record.startedAt;
    record.bytes = Number.isFinite(length) && length > 0 ? length : null;
    record.retries = config.__retryCount || 0;

    if (page && page.requestIds.includes(config.__perfRequestId)) {
        page.inflight = Math.max(0, page.inflight - 1);
        page.lastActivityAt = now();
    }
};

const startObservers = () => {
    const observe = (type, handle) => {
        try {
            new PerformanceObserver(list => {
                if (page) {
                    list.getEntries().forEach(entry => handle(entry, page.counters));
                }
            }).observe({ type, buffered: true });
        } catch {
            // The entry type is unsupported in this browser.
        }
    };

    observe('longtask', (entry, counters) => {
        counters.longTasks.push({
            start_ms: Math.round(entry.startTime - (page?.startedAt ?? 0)),
            duration_ms: Math.round(entry.duration),
        });
    });
    observe('layout-shift', (entry, counters) => {
        if (!entry.hadRecentInput) {
            counters.layoutShift += entry.value;
        }
    });
};

const countComponent = (instance, bucket) => {
    if (!page) {
        return;
    }

    const options = instance.$options || {};
    const name = options.name || options.__name || 'Anonymous';

    page.counters[bucket][name] = (page.counters[bucket][name] || 0) + 1;
};

/**
 * Wire the recorder into the app. A no-op when diagnostics are off.
 *
 * @param {import('vue').App} app
 * @param {import('vue-router').Router} router
 * @param {import('axios').AxiosInstance} httpClient shared axios instance used for the report POST
 */
export const installPerfDiagnostics = (app, router, httpClient) => {
    if (!isPerfEnabled()) {
        return;
    }

    http = httpClient;
    startPage({ kind: 'full-load' });
    startObservers();

    app.mixin({
        mounted() {
            countComponent(this, 'mounted');
        },
        updated() {
            countComponent(this, 'updated');
        },
    });

    router?.isReady?.().then(markNavigationEnd);
};

/** Test helper: forget all module state. */
export const resetPerfDiagnostics = () => {
    stopPolling();
    enabled = null;
    http = null;
    reportingStopped = false;
    page = null;
    requestSequence = 0;
    requests.clear();
};
